import { getActions, getGlobal, setGlobal } from '../global';

import type {
  ApiChat, ApiMessage, ApiPeer, ApiPeerReaction,
  ApiPhoneCall, ApiUser,
} from '../api/types';
import type { TauriMessageNotification, TauriShownMessageNotification } from '../types/tauri';
import { ApiMediaFormat } from '../api/types';

import { APP_NAME, DEBUG, IS_TEST } from '../config';
import {
  getChatAvatarHash,
  getChatTitle,
  getMessageRecentReaction,
  getUserFullName,
} from '../global/helpers';
import {
  getIsChatMuted,
  getIsChatSilent,
  getShouldIgnoreNotificationMute,
  getShouldShowMessagePreview,
} from '../global/helpers/notifications';
import { getMessageSenderName } from '../global/helpers/peers';
import {
  selectCurrentMessageList,
  selectCustomEmoji,
  selectIsChatWithSelf,
  selectNotifyDefaults,
  selectNotifyException,
  selectPeer,
  selectSender,
  selectSettingsKeys,
  selectTopicFromMessage,
} from '../global/selectors';
import { callApi } from '../api/client';
import { IS_TAURI } from './browser/globalEnvironment';
import { IS_SERVICE_WORKER_SUPPORTED, IS_TOUCH_ENV } from './browser/windowEnvironment';
import jsxToHtml from './element/jsxToHtml';
import getIsAppFocused from './tauri/windowFocus';
import { buildCollectionByKey } from './iteratees';
import { getTranslationFn } from './localization';
import * as mediaLoader from './mediaLoader';
import { oldTranslate } from './oldLangProvider';
import { debounce } from './schedulers';
import { getServerTime } from './serverTime';

// What each message banner showed, so it can be closed when the message is recalled or deleted: the
// desktop shell finds a macOS banner by its text, and the text is gone from the message by then.
const MAX_SHOWN_MESSAGE_NOTIFICATIONS = 500;
const shownMessageNotifications = new Map<string, TauriShownMessageNotification & { local?: Notification }>();

function rememberMessageNotification(shown: TauriShownMessageNotification & { local?: Notification }) {
  const key = `${shown.chatId}_${shown.messageId}`;
  shownMessageNotifications.delete(key);
  shownMessageNotifications.set(key, shown);
  if (shownMessageNotifications.size > MAX_SHOWN_MESSAGE_NOTIFICATIONS) {
    shownMessageNotifications.delete(shownMessageNotifications.keys().next().value!);
  }
}

function getDeviceToken(subscription: PushSubscription) {
  const data = subscription.toJSON();
  const endpoint = data.endpoint ?? '';
  // With `gcm_sender_id` in the web manifest, Chromium subscribes against FCM and the endpoint is
  // `https://fcm.googleapis.com/fcm/send/<registration-token>`. The backend's notification service
  // delivers via FCM (firebase-admin), which needs that registration token — so register the
  // token (the endpoint's last path segment) rather than the raw Web Push subscription.
  // (The manifest's `gcm_sender_id` must match the notification service's FCM project for delivery.)
  // Non-FCM browsers (Firefox/Safari) have no FCM token; fall back to the subscription JSON, which
  // this FCM-only service can't deliver to — harmless, just not pushed.
  if (endpoint.includes('fcm.googleapis.com') || endpoint.includes('android.googleapis.com')) {
    return endpoint.substring(endpoint.lastIndexOf('/') + 1);
  }
  return JSON.stringify({
    endpoint: data.endpoint,
    keys: data.keys,
  });
}

function checkIfPushSupported() {
  if (!IS_SERVICE_WORKER_SUPPORTED || IS_TAURI) return false;

  if (!('showNotification' in ServiceWorkerRegistration.prototype)) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.warn('[PUSH] Push notifications aren\'t supported.');
    }
    return false;
  }

  // If permission is denied, it is blocked until the user manually changes their settings
  if (Notification.permission === 'denied') {
    return false;
  }

  // Check if push messaging is supported
  if (!('PushManager' in window)) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.warn('[PUSH] Push messaging isn\'t supported.');
    }
    return false;
  }

  return true;
}

// Returns whether the desktop shell took the notification. An older shell has no such command, in
// which case the caller must fall back to the `window.Notification` shim.
async function showTauriNotification(notification: TauriMessageNotification) {
  try {
    await window.tauri.showMessageNotification(notification);
    return true;
  } catch (error) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.warn('[PUSH] Desktop shell cannot show notifications.', error);
    }
    return false;
  }
}

export function checkIfNotificationsSupported() {
  // The desktop shell delivers banners through its own command rather than the `window.Notification`
  // shim, whose permission state only settles after an async IPC round-trip.
  if (IS_TAURI) return true;

  // Let's check if the browser supports notifications
  if (!('Notification' in window)) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.warn('[PUSH] This browser does not support desktop notification');
    }
    return false;
  }

  if (Notification.permission === 'denied') {
    return false;
  }
  return true;
}

const expirationTime = 12 * 60 * 60 * 1000; // 12 hours
// Notification id is removed from soundPlayed cache after 3 seconds
const soundPlayedDelay = 3 * 1000;
// Fallback volume (0-10 scale) when the persisted setting is missing/invalid
const DEFAULT_NOTIFICATION_SOUND_VOLUME = 5;
const soundPlayedIds = new Set<string>();
// Root-relative: a deep link opens the app at `/c/<id>/<msg>`, where a relative path resolves
// against that directory and 404s, leaving every notification silent.
const notificationSound = new Audio('/notification.mp3');
notificationSound.setAttribute('mozaudiochannel', 'notification');

// Browsers block `Audio.play()` outside a user gesture, so the sound triggered by an incoming
// message is silently rejected. Prime the element muted on the first user interaction to unlock it
// for later programmatic playback.
const PRIME_SOUND_EVENTS = ['pointerdown', 'keydown', 'touchstart'];
let isNotificationSoundPrimed = false;

function primeNotificationSound() {
  if (isNotificationSoundPrimed) return;
  isNotificationSoundPrimed = true;

  const previousVolume = notificationSound.volume;
  notificationSound.muted = true;
  void notificationSound.play()
    .then(() => {
      notificationSound.pause();
      notificationSound.currentTime = 0;
    })
    .catch(() => undefined)
    .finally(() => {
      notificationSound.muted = false;
      notificationSound.volume = previousVolume;
    });
}

if (typeof document !== 'undefined') {
  const unlockNotificationSound = () => {
    primeNotificationSound();
    PRIME_SOUND_EVENTS.forEach((event) => document.removeEventListener(event, unlockNotificationSound));
  };
  PRIME_SOUND_EVENTS.forEach((event) => {
    document.addEventListener(event, unlockNotificationSound, { once: true, passive: true });
  });
}

export async function playNotifySound(id?: string, volume?: number) {
  const { notificationSoundVolume } = selectSettingsKeys(getGlobal());
  // Guard against a missing/invalid persisted volume — setting `audio.volume = NaN` throws.
  const rawVolume = volume || notificationSoundVolume;
  const currentVolume = Number.isFinite(rawVolume)
    ? Math.min(Math.max(rawVolume / 10, 0), 1)
    : DEFAULT_NOTIFICATION_SOUND_VOLUME / 10;

  if (id !== undefined && soundPlayedIds.has(id)) return;
  if (currentVolume === 0) return;
  notificationSound.volume = currentVolume;
  if (id !== undefined) {
    notificationSound.addEventListener('ended', () => {
      soundPlayedIds.add(id);
    }, { once: true });

    setTimeout(() => {
      soundPlayedIds.delete(id);
    }, soundPlayedDelay);
  }

  try {
    await notificationSound.play();
  } catch (error) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.warn('[PUSH] Unable to play notification sound');
    }
  }
}

export const playNotifySoundDebounced = debounce(playNotifySound, 1000, true, false);

function checkIfShouldResubscribe(subscription: PushSubscription | null) {
  const global = getGlobal();
  if (!global.push || !subscription) return true;
  if (getDeviceToken(subscription) !== global.push.deviceToken) return true;
  return Date.now() - global.push.subscribedAt > expirationTime;
}

export async function requestPermission() {
  if (IS_TAURI) {
    const tauriPlugin = await import('@tauri-apps/plugin-notification');
    const tauriPermissionGranted = await tauriPlugin.isPermissionGranted();

    if (!tauriPermissionGranted) {
      const permission = await tauriPlugin.requestPermission();

      return permission === 'granted';
    }

    return true;
  }

  if (!('Notification' in window)) {
    return false;
  }
  let permission = Notification.permission;
  if (!['granted', 'denied'].includes(permission)) {
    permission = await Notification.requestPermission();
  }
  return permission === 'granted';
}

async function unsubscribeFromPush(subscription: PushSubscription | null) {
  const { deleteDeviceToken } = getActions();
  if (subscription) {
    try {
      const deviceToken = getDeviceToken(subscription);
      await callApi('unregisterDevice', deviceToken);
      await subscription.unsubscribe();
      deleteDeviceToken();
      return;
    } catch (error) {
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.log('[PUSH] Unable to unsubscribe from push.', error);
      }
    }
  }
  const global = getGlobal();
  if (global.push) {
    await callApi('unregisterDevice', global.push.deviceToken);
    deleteDeviceToken();
  }
}

export async function unsubscribe() {
  if (!checkIfPushSupported()) return;
  const serviceWorkerRegistration = await navigator.serviceWorker.ready;
  const subscription = await serviceWorkerRegistration.pushManager.getSubscription();
  await unsubscribeFromPush(subscription);
}

// Load custom emoji from the api if it's not cached already
async function loadCustomEmoji(id: string) {
  let global = getGlobal();
  if (selectCustomEmoji(global, id)) return;
  const customEmoji = await callApi('fetchCustomEmoji', {
    documentId: [id],
  });
  if (!customEmoji) return;
  global = getGlobal();
  global = {
    ...global,
    customEmojis: {
      ...global.customEmojis,
      byId: {
        ...global.customEmojis.byId,
        ...buildCollectionByKey(customEmoji, 'id'),
      },
    },
  };
  setGlobal(global);
}

let isSubscriptionFailed = false;
export function checkIfOfflinePushFailed() {
  return isSubscriptionFailed;
}

export async function subscribe() {
  const { setDeviceToken, updateWebNotificationSettings } = getActions();
  let hasWebNotifications = false;
  let hasPushNotifications = false;
  if (!checkIfPushSupported()) {
    // Ask for notification permissions only if service worker notifications are not supported
    // As pushManager.subscribe automatically triggers permission popup
    hasWebNotifications = await requestPermission();
    updateWebNotificationSettings({
      hasWebNotifications,
      hasPushNotifications,
    });
    return;
  }
  const serviceWorkerRegistration = await navigator.serviceWorker.ready;
  let subscription = await serviceWorkerRegistration.pushManager.getSubscription();
  if (!checkIfShouldResubscribe(subscription)) return;
  await unsubscribeFromPush(subscription);
  try {
    subscription = await serviceWorkerRegistration.pushManager.subscribe({
      userVisibleOnly: true,
    });
    const deviceToken = getDeviceToken(subscription);
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[PUSH] Received push subscription: ', deviceToken);
    }
    await callApi('registerDevice', deviceToken);
    setDeviceToken({ token: deviceToken });
    hasPushNotifications = true;
    hasWebNotifications = true;
  } catch (error: any) {
    if (Notification.permission === 'denied') {
      // The user denied the notification permission which
      // means we failed to subscribe and the user will need
      // to manually change the notification permission to
      // subscribe to push messages (no-op — expected; user must re-enable in browser settings)
    } else {
      // A problem occurred with the subscription, this can
      // often be down to an issue or lack of the gcm_sender_id
      // and / or gcm_user_visible_only
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.log('[PUSH] Unable to subscribe to push.', error);
      }
      // Push subscription failed (e.g. missing VAPID key or no push backend). Fall back to local
      // notifications by requesting the browser permission directly, regardless of the error code.
      isSubscriptionFailed = true;
      hasWebNotifications = await requestPermission();
    }
  }
  updateWebNotificationSettings({
    hasWebNotifications,
    hasPushNotifications,
  });
}

function checkIfShouldNotify(chat: ApiChat, message: Partial<ApiMessage>) {
  const global = getGlobal();
  const isChatMuted = getIsChatMuted(chat, selectNotifyDefaults(global), selectNotifyException(global, chat.id));
  const topic = selectTopicFromMessage(global, message as ApiMessage);
  const topicMutedUntil = topic?.notifySettings.mutedUntil;
  const isMuted = topicMutedUntil === undefined ? isChatMuted : topicMutedUntil > getServerTime();
  const shouldNotifyAboutPinnedMessages = global.settings.byKey.shouldNotifyAboutPinnedMessages;
  const shouldIgnoreMute = getShouldIgnoreNotificationMute(message, shouldNotifyAboutPinnedMessages);

  const isPinMessage = message.content?.action?.type === 'pinMessage';
  const shouldSkipPinnedMessageNotification = isPinMessage && !shouldNotifyAboutPinnedMessages;
  const shouldNotifyAboutMessage = message.content?.action?.type !== 'phoneCall';
  if (shouldSkipPinnedMessageNotification || (isMuted && !shouldIgnoreMute) || !shouldNotifyAboutMessage
    || chat.isNotJoined || !chat.isListed || selectIsChatWithSelf(global, chat.id)) {
    return false;
  }
  // On touch devices show notifications when chat is not active
  if (IS_TOUCH_ENV) {
    const {
      chatId,
      type,
    } = selectCurrentMessageList(global) || {};
    return !(chatId === chat.id && type === 'thread');
  }
  // On desktop show notifications when window is not focused
  return !getIsAppFocused();
}

// Async only to load `MessageSummary` on demand — see the import inside. Every caller already
// awaits (notifyAboutMessage awaits the avatar fetch immediately above its call), so this costs no
// extra round of latency.
async function getNotificationContent(chat: ApiChat, message: ApiMessage, reaction?: ApiPeerReaction) {
  const global = getGlobal();
  let sender = selectSender(global, message);
  const hasReaction = Boolean(reaction);
  if (hasReaction) {
    sender = selectPeer(global, reaction.peerId);
  }

  const { isScreenLocked } = global.passcode;
  const isSelf = chat.id === global.currentUserId;

  let body: string;
  if (
    !isScreenLocked
    && getShouldShowMessagePreview(chat, selectNotifyDefaults(global), selectNotifyException(global, chat.id))
  ) {
    const senderName = sender ? getMessageSenderName(getTranslationFn(), chat.id, sender) : undefined;
    // Loaded here rather than imported at the top of the file. This module is reachable from the
    // entry (the global actions import it), and `MessageSummary` statically pulls in the whole
    // message-rendering subtree — ActionMessageText, MessageText and their assets, 75 modules and
    // ~97 KB of source — so every session parsed the message renderer at boot to format a
    // notification it might never show. Inside this branch it is not even loaded when the screen is
    // locked or previews are off.
    const { default: MessageSummary } = await import('../components/common/MessageSummary');
    let summary = jsxToHtml(<span><MessageSummary message={message} /></span>)[0].textContent || '';

    if (hasReaction) {
      const emoji = getReactionEmoji(reaction);
      summary = oldTranslate('PushReactText', [emoji, summary]);
    }

    body = senderName ? `${senderName}: ${summary}` : summary;
  } else {
    body = getTranslationFn()('NotificationMessageTextHidden');
  }

  let title = isScreenLocked ? APP_NAME : getChatTitle(oldTranslate, chat, isSelf);

  if (message.isSilent) {
    title += ' 🔕';
  }

  return { title, body };
}

async function getAvatar(chat: ApiPeer) {
  const imageHash = getChatAvatarHash(chat);
  if (!imageHash) return undefined;
  let mediaData = mediaLoader.getFromMemory(imageHash);
  if (!mediaData) {
    await mediaLoader.fetch(imageHash, ApiMediaFormat.BlobUrl);
    mediaData = mediaLoader.getFromMemory(imageHash);
  }
  return mediaData;
}

function getReactionEmoji(reaction: ApiPeerReaction) {
  let emoji;
  if (reaction.reaction.type === 'emoji') {
    emoji = reaction.reaction.emoticon;
  }

  if (reaction.reaction.type === 'custom') {
    emoji = selectCustomEmoji(getGlobal(), reaction.reaction.documentId)?.emoji;
  }
  return emoji || '❤️';
}

export async function notifyAboutCall({
  call, user,
}: {
  call: ApiPhoneCall; user: ApiUser;
}) {
  const { hasWebNotifications } = selectSettingsKeys(getGlobal());
  if (getIsAppFocused() || !hasWebNotifications) return;
  const areNotificationsSupported = checkIfNotificationsSupported();
  if (!areNotificationsSupported) return;

  if (IS_TAURI && await showTauriNotification({
    title: oldTranslate('VoipIncoming'),
    body: getUserFullName(user) || '',
    isSilent: false,
  })) {
    return;
  }

  const icon = await getAvatar(user);

  const options: NotificationOptions = {
    body: getUserFullName(user),
    icon,
    badge: icon,
    tag: `call_${call.id}`,
  };

  if ('vibrate' in navigator) {
    // @ts-ignore
    options.vibrate = [200, 100, 200];
  }

  const notification = new Notification(oldTranslate('VoipIncoming'), options);

  notification.onclick = () => {
    notification.close();
    if (window.focus) {
      window.focus();
    }
  };
}

export async function notifyAboutMessage({
  chat,
  message,
  isReaction = false,
}: { chat: ApiChat; message: Partial<ApiMessage>; isReaction?: boolean }) {
  const global = getGlobal();
  const { hasWebNotifications } = selectSettingsKeys(global);
  if (!checkIfShouldNotify(chat, message)) return;
  const isChatSilent = getIsChatSilent(
    chat, selectNotifyDefaults(getGlobal()), selectNotifyException(getGlobal(), chat.id),
  );
  const topic = selectTopicFromMessage(global, message as ApiMessage);
  const isSilent = topic?.notifySettings.hasSound === undefined ? isChatSilent : !topic.notifySettings.hasSound;

  const areNotificationsSupported = checkIfNotificationsSupported();
  if (!hasWebNotifications || !areNotificationsSupported) {
    if (!isSilent && !message.isSilent && !isReaction) {
      // Only play sound if web notifications are disabled
      playNotifySoundDebounced(String(message.id) || chat.id);
    }

    return;
  }

  if (!message.id) return;

  const activeReaction = getMessageRecentReaction(message);
  // Do not notify about reactions on messages that are not outgoing
  if (isReaction && !activeReaction) return;

  // If this is a custom emoji reaction we need to make sure it is loaded
  if (isReaction && activeReaction && activeReaction.reaction.type === 'custom') {
    await loadCustomEmoji(activeReaction.reaction.documentId);
  }

  // The desktop shell cannot render an icon, so skip fetching one for it
  const icon = IS_TAURI ? undefined : await getAvatar(chat);

  const {
    title,
    body,
  } = await getNotificationContent(chat, message as ApiMessage, activeReaction);

  // The shell plays the system sound itself, so this path skips the client-side one
  if (IS_TAURI && await showTauriNotification({
    title,
    body,
    isSilent: Boolean(isSilent || message.isSilent),
    clickPayload: { chatId: chat.id, messageId: message.id },
  })) {
    rememberMessageNotification({
      chatId: chat.id, messageId: message.id, title, body,
    });
    return;
  }

  const shouldPlaySound = !isSilent && !isReaction && !message.isSilent;

  // Show the banner via the service worker when it can actually display it (needs an active
  // controller and a granted permission); otherwise show a local notification directly. The sound
  // is always played client-side below, so it no longer depends on the fragile worker round-trip
  // (the shared debounce collapses the worker's own sound call with ours into a single play).
  const canDelegateToServiceWorker = checkIfPushSupported()
    && Notification.permission === 'granted'
    && Boolean(navigator.serviceWorker?.controller);

  if (canDelegateToServiceWorker) {
    // notify service worker about new message notification
    rememberMessageNotification({
      chatId: chat.id, messageId: message.id, title, body,
    });
    navigator.serviceWorker.controller!.postMessage({
      type: 'showMessageNotification',
      payload: {
        title,
        body,
        icon,
        chatId: chat.id,
        messageId: message.id,
        shouldReplaceHistory: true,
        isSilent: isSilent || message.isSilent,
        reaction: activeReaction?.reaction,
      },
    });
  } else if (Notification.permission === 'granted') {
    // Local notification fallback when the service worker is not controlling the page yet or push
    // is unavailable (e.g. no push backend configured).
    const dispatch = getActions();
    const options: NotificationOptions = {
      body,
      icon,
      badge: icon,
      tag: String(message.id),
    };

    if ('vibrate' in navigator) {
      // @ts-ignore
      options.vibrate = [200, 100, 200];
    }

    const notification = new Notification(title, options);
    rememberMessageNotification({
      chatId: chat.id, messageId: message.id, title, body, local: notification,
    });

    notification.onclick = () => {
      notification.close();
      dispatch.focusMessage({
        chatId: chat.id,
        messageId: message.id!,
        shouldReplaceHistory: true,
      });
      if (window.focus) {
        window.focus();
      }
    };
  }

  if (shouldPlaySound) {
    playNotifySoundDebounced(String(message.id) || chat.id);
  }
}

export function closeMessageNotifications(payload: { chatId: string; lastReadInboxMessageId?: number }) {
  if (IS_TEST || !navigator.serviceWorker?.controller) return;
  navigator.serviceWorker.controller.postMessage({
    type: 'closeMessageNotifications',
    payload,
  });
}

// A recalled or deleted message must not stay readable in a banner. The service worker closes its own
// by id; the desktop shell and a local fallback banner are closed from what was remembered when shown.
export function closeRemovedMessageNotifications(chatId: string, messageIds: number[]) {
  if (IS_TEST) return;

  messageIds.forEach((messageId) => {
    const key = `${chatId}_${messageId}`;
    const shown = shownMessageNotifications.get(key);
    if (!shown) return;
    shownMessageNotifications.delete(key);

    const { local, ...notification } = shown;
    local?.close();
    if (IS_TAURI) {
      // An older shell has no such command; its banner stays until dismissed
      window.tauri.closeMessageNotification(notification).catch(() => undefined);
    }
  });

  if (!IS_TAURI && navigator.serviceWorker?.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'closeMessageNotifications',
      payload: { chatId, messageIds },
    });
  }
}

// Notify service worker that client is fully loaded
export function notifyClientReady() {
  if (!navigator.serviceWorker?.controller) return;
  navigator.serviceWorker.controller.postMessage({
    type: 'clientReady',
  });
}
